/**
 * The web colour theme (CLA-1894, John 2026-10-26): Navy, the brand and the
 * default, or Green, the pre-rebrand Claro green. One per user, stored on the
 * account (`color_theme` on /auth/me and /settings), so it follows them to any
 * browser. The same two choices in AU and US.
 *
 * A theme is `data-accent="<name>"` on <html>: claro-tokens.css swaps the
 * claro-50…900 scale and the primary, link and focus roles under it. Navy is
 * the absence of the attribute, so a page that never runs this looks exactly
 * as it always did. Background, text, status colours, record red and the
 * Ledger logo are the same in every theme.
 *
 * localStorage holds the last value only as a boot hint: index.html reads it
 * before first paint so a green user never sees a navy flash, and /auth/me
 * then reconciles it with the account. Only the signed-in app shell is ever
 * painted: see THEME_FREE_PATHS. The key sits under `claro:theme`, which
 * sign-out keeps (it is a UI preference, no PHI).
 */

export const COLOR_THEMES = ['navy', 'green'] as const
export type ColorTheme = (typeof COLOR_THEMES)[number]

export const DEFAULT_COLOR_THEME: ColorTheme = 'navy'

/** Read by the inline script in index.html too: keep the two in step. */
export const COLOR_THEME_STORAGE_KEY = 'claro:theme:accent'

/** Anything the server or storage might hold, as a theme. Unknown → navy. */
export function normaliseColorTheme(value: unknown): ColorTheme {
  return (COLOR_THEMES as readonly unknown[]).includes(value) ? (value as ColorTheme) : DEFAULT_COLOR_THEME
}

/** The theme painted right now. */
export function currentColorTheme(root: HTMLElement = document.documentElement): ColorTheme {
  return normaliseColorTheme(root.getAttribute('data-accent') ?? DEFAULT_COLOR_THEME)
}

/**
 * A clinician's colour is theirs, never their patients'. Signed-out and
 * patient-facing pages (booking, the portal, consent links, sign-in, a clinic
 * iPad handed across the desk) always paint the brand, whatever this browser
 * cached. index.html carries a copy of this list for the boot hint:
 * `color-theme.test.ts` keeps the two in step.
 */
export const THEME_FREE_PATHS = [
  '/login', '/logout', '/signup', '/forgot-password', '/reset-password',
  '/consent', '/book', '/embed', '/booking', '/p', '/portal', '/meet',
] as const

/** True on the signed-in app's own pages; false on any page a patient or a stranger sees. */
export function colorThemeAllowedAt(pathname: string): boolean {
  return !THEME_FREE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** Patient-facing surfaces open inside the app (in-clinic consent signing). */
let suspended = 0

function cachedColorTheme(): ColorTheme {
  try {
    return normaliseColorTheme(localStorage.getItem(COLOR_THEME_STORAGE_KEY) ?? DEFAULT_COLOR_THEME)
  } catch {
    return DEFAULT_COLOR_THEME
  }
}

function paint(theme: ColorTheme, root: HTMLElement): void {
  const allowed = suspended === 0 && colorThemeAllowedAt(window.location.pathname)
  if (theme === DEFAULT_COLOR_THEME || !allowed) root.removeAttribute('data-accent')
  else root.setAttribute('data-accent', theme)
}

/**
 * Remember `theme` (the next boot's hint) and paint it, but only on the
 * signed-in app's pages with no patient-facing surface open.
 */
export function applyColorTheme(theme: unknown, root: HTMLElement = document.documentElement): ColorTheme {
  const t = normaliseColorTheme(theme)
  try {
    if (t === DEFAULT_COLOR_THEME) localStorage.removeItem(COLOR_THEME_STORAGE_KEY)
    else localStorage.setItem(COLOR_THEME_STORAGE_KEY, t)
  } catch {
    /* storage unavailable (private mode): the account still holds the choice */
  }
  paint(t, root)
  return t
}

/** Repaint from the cache for the current page (e.g. on entering or leaving the app shell). */
export function syncColorTheme(root: HTMLElement = document.documentElement): void {
  paint(cachedColorTheme(), root)
}

/** Back to the brand everywhere, e.g. leaving the signed-in app for a public page. */
export function clearPaintedColorTheme(root: HTMLElement = document.documentElement): void {
  root.removeAttribute('data-accent')
}

/**
 * Paint the brand while a patient-facing surface is open inside the app (the
 * patient signs a consent on the clinician's iPad). Returns the undo.
 */
export function suspendColorTheme(root: HTMLElement = document.documentElement): () => void {
  suspended++
  root.removeAttribute('data-accent')
  let done = false
  return () => {
    if (done) return
    done = true
    suspended = Math.max(0, suspended - 1)
    paint(cachedColorTheme(), root)
  }
}
